import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { get } from "../../utils/fetchAPI";
import PageTitle from "../../components/page-title/pageTitle";
import AddLocations from "../addLocations/addLocations";
import Loader from "../../components/loader/loder";
import { toast, ToastContainer } from "react-toastify";
import DataTable from "react-data-table-component";
import { TbLocation } from "react-icons/tb";
import { IoArrowUndo } from "react-icons/io5";

const LocationDetails = () => {
    const [locations, setLocations] = useState([]);
    const { id } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();

    const organization = state?.organization;
    const organizationId = organization?._id;
    const fromPage = state?.fromPage || 1;

    const [editLocationData, setEditLocationData] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [loading, setLoading] = useState(false);

    const fetchLocations = useCallback(async () => {
        try {
            setLoading(true);
            const response = await get(`location/list/${id}`);
            setLocations(response?.data?.locations || []);
        } catch (error) {
            console.error("Error fetching locations:", error);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    const handleEdit = (location) => {
        setEditLocationData(location);
        setShowPopup(true);
    };

    const handlePopupClose = () => {
        setEditLocationData(null);
        setShowPopup(false);
    };

    /* =======================
       TABLE COLUMNS
    ======================== */

    const columns = [
        {
            name: "Location Name",
            selector: row => row?.name || "-",
            sortable: true,
        },
        {
            name: "Address",
            selector: row => row?.address?.text || "-",
            wrap: true,
        },
        {
            name: "Contact Information",
            cell: row => (
                <div className="text-sm leading-6">
                    {row?.contact?.length > 0 ? (
                        row.contact.map((contact, idx) => (
                            <div key={idx} className="mb-2">
                                <div>
                                    <strong>Purpose:</strong> {contact.purpose}
                                </div>
                                <div>
                                    <strong>Name:</strong> {contact.name}
                                </div>
                                {contact.contact.map((detail, dIdx) => (
                                    <div key={dIdx}>
                                        <strong>{detail.system}:</strong> {detail.value}
                                    </div>
                                ))}
                            </div>
                        ))
                    ) : (
                        <span className="text-gray-400">N/A</span>
                    )}
                </div>
            ),
        },
        {
            name: "Hours of Operation",
            cell: row => (
                <div className="text-sm leading-6">
                    {row?.hoursOfOperation?.availableTime?.length > 0 ? (
                        row.hoursOfOperation.availableTime.map((time, idx) => (
                            <div key={idx} className="mb-2">
                                <div>
                                    <strong>Day:</strong> {time.daysOfWeek}
                                </div>
                                {time.allDay ? (
                                    <div>All day</div>
                                ) : (
                                    <ul className="list-disc pl-4">
                                        {time.visitHours.map((hours, hIdx) => (
                                            <li key={hIdx}>
                                                {hours.availableStartTime} - {hours.availableEndTime}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))
                    ) : (
                        <span className="text-gray-400">N/A</span>
                    )}
                </div>
            ),
        },
        {
            name: "Actions",
            width: "120px",
            center: true,
            cell: row => (
                <button
                    onClick={() => handleEdit(row)}
                    className="px-3 py-1 bg-secondary text-white rounded text-sm"
                >
                    Edit
                </button>
            ),
        },
    ];

    /* =======================
       TABLE STYLES
    ======================== */

    const customStyles = {
        table: {
            style: {
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
            },
        },
        headCells: {
            style: {
                backgroundColor: "#f3f4f6",
                fontWeight: "700",
                fontSize: "1rem",
                borderBottom: "1px solid #e5e7eb",
            },
        },
        cells: {
            style: {
                paddingTop: "12px",
                paddingBottom: "12px",
                verticalAlign: "top",
            },
        },
    };

    return (
        <div className="flex flex-col gap-5">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <IoArrowUndo
                        onClick={() =>
                            navigate(`/organization-list?page=${fromPage}`)
                        }
                        className="h-8 w-8 text-secondary cursor-pointer"
                    />
                    <PageTitle title="Location Details" icon={<TbLocation />} />
                </div>

                <button
                    onClick={() => setShowPopup(true)}
                    className="text-sm px-4 py-2 bg-primary text-white rounded-md"
                >
                    Add Location
                </button>
            </div>

            {/* Organization Info */}
            <div className="p-6 bg-white shadow rounded">
                <h1 className="text-2xl font-bold">{organization?.name}</h1>
                <p>{organization?.alias}</p>
            </div>

            {/* TABLE */}
            {loading ? (
                <div className="flex justify-center py-10">
                    <Loader />
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={locations}
                    striped
                    highlightOnHover
                    persistTableHead
                    customStyles={customStyles}
                    noDataComponent={
                        <div className="py-6 text-gray-500">
                            No locations found
                        </div>
                    }
                />
            )}

            {/* MODAL */}
            {showPopup && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white p-5 rounded-md shadow-lg">
                        <AddLocations
                            modalTitle={
                                editLocationData ? "Edit Location" : "Add Location"
                            }
                            organizationId={organizationId}
                            onClose={handlePopupClose}
                            onAddLocation={fetchLocations}
                            initialData={editLocationData}
                            onAddLocationSuccess={() =>
                                toast.success("Location updated successfully!")
                            }
                        />
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                style={{ zIndex: 99999 }}
            />
        </div>
    );
};

export default LocationDetails;
